import { useRef } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { MoveRight } from 'lucide-react-native'
import { calculateStampPreview, colors } from '@chillout/core'
import { AvatarInfo, Button } from '@chillout/ui'
import { AdminModal, ModalActions } from '../shared/Modal'

interface StampAdjustConfirmModalProps {
  visible: boolean
  memberName: string
  memberEmail: string
  currentStamps: number
  stampDelta: number
  stampTarget: number
  reason: string
  note: string
  onConfirm: () => void
  onCancel: () => void
}

export function StampAdjustConfirmModal({
  visible,
  memberName,
  memberEmail,
  currentStamps,
  stampDelta,
  stampTarget,
  reason,
  note,
  onConfirm,
  onCancel,
}: StampAdjustConfirmModalProps) {
  const { t } = useTranslation()

  const snapshot = useRef({ memberName, memberEmail, currentStamps, stampDelta, stampTarget, reason, note })
  if (visible) {
    snapshot.current = { memberName, memberEmail, currentStamps, stampDelta, stampTarget, reason, note }
  }
  const s = snapshot.current
  const isRemoving = s.stampDelta < 0
  const abs = Math.abs(s.stampDelta)

  const title = isRemoving
    ? t('members.detail.stampAdjustment.confirmTitle_remove', { count: abs })
    : t('members.detail.stampAdjustment.confirmTitle_add', { count: abs })

  const confirmLabel = isRemoving
    ? t('members.detail.stampAdjustment.confirmButton_remove', { count: abs }) as string
    : t('members.detail.stampAdjustment.confirmButton_add', { count: abs }) as string

  const body = isRemoving
    ? t('members.detail.stampAdjustment.confirmBody_remove', { count: abs })
    : t('members.detail.stampAdjustment.confirmBody_add', { count: abs })

  const afterStamps = isRemoving
    ? Math.max(0, s.currentStamps + s.stampDelta)
    : (() => {
        const preview = calculateStampPreview(s.currentStamps, s.stampDelta, s.stampTarget)
        return preview.rewardsUnlocked > 0 ? preview.carryOver : s.currentStamps + s.stampDelta
      })()

  const reasonLabel = t(`common.adjustmentReasons.${s.reason}`, { defaultValue: s.reason })

  return (
    <AdminModal
      visible={visible}
      onClose={onCancel}
      closeAccessibilityLabel={t('common.cancel')}
      title={title}
    >
      <View className="mt-4 gap-4">
        <AvatarInfo name={s.memberName} email={s.memberEmail} />

        <Text className="text-sm font-normal text-secondary">{body}</Text>

        <View className="flex-row items-center justify-center rounded-lg bg-canvas-primary px-4 py-3">
          <View className="flex-row items-baseline gap-1">
            <Text className="text-base font-bold text-primary">{s.currentStamps}</Text>
            <Text className="text-base font-bold text-primary">/ {s.stampTarget}</Text>
          </View>
          <View className="px-3">
            <MoveRight size={16} color={colors.text.secondary} strokeWidth={2} />
          </View>
          <View className="flex-row items-baseline gap-1">
            <Text className="text-base font-bold text-accent">{afterStamps}</Text>
            <Text className="text-base font-bold text-primary">/ {s.stampTarget}</Text>
          </View>
        </View>

        <View className="gap-2">
          <View className="flex-row gap-2">
            <Text className="text-sm font-semibold text-primary">{t('members.detail.stampAdjustment.reason')}:</Text>
            <Text className="text-sm font-normal text-primary">{reasonLabel}</Text>
          </View>
          {s.note ? (
            <View className="flex-row gap-2">
              <Text className="text-sm font-semibold text-primary">{t('members.detail.stampAdjustment.note')}:</Text>
              <Text className="min-w-0 flex-1 text-sm font-normal text-secondary">{s.note}</Text>
            </View>
          ) : null}
        </View>

      </View>

      <View className="mt-6">
        <ModalActions>
          <Button
            label={t('common.cancel')}
            variant="secondary"
            fullWidth={false}
            onPress={onCancel}
          />
          <Button
            label={confirmLabel}
            variant="primary"
            className="flex-1"
            onPress={onConfirm}
          />
        </ModalActions>
      </View>
    </AdminModal>
  )
}